import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Tab from 'react-bootstrap/Tab';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { setNewRewardsCount } from 'components/RewardsButton/actions';
import { selectNewRewardsCount } from 'components/RewardsButton/selectors';
import TabView from 'components/TabView';
import { getPreferredLocationData } from 'utils/constants';
import { LG_BREAKPOINT_AND_UP } from 'utils/constants';
import { isLocationPls } from 'utils/locations';
import {
  isAvailable,
  isExpired,
  isRedeemed,
  isStandardPrizeType
} from 'utils/rewards';
import {
  errorShape,
  onlineOfferPropTypes,
  partnerRewardPropTypes,
  patronRewardPropTypes,
  plsRewardPropTypes,
  userShape
} from 'utils/shapes';
import useMediaQuery from 'utils/useMediaQuery';
import GetMoreRewardsCard from './GetMoreRewardsCard';
import ProfileRewardCard from './ProfileRewardCard';
import './ProfileRewards.scss';

const CURRENT = 'current';
const PAST = 'past';

const TABS = [
  { key: CURRENT, title: 'Current' },
  { key: PAST, title: 'Past' }
];

const ProfileRewards = ({
  patronRewardsData,
  plsRewardsData,
  resetNewState,
  newRewardsCount,
  subscribeToNewRewards = () => {},
  subscribeToNewPLSRewards = () => {},
  setNewRewardsCount,
  onlineOffers,
  patronRewards,
  partnerRewards,
  plsRewards,
  userData,
  redeemOnlineOffer
}) => {
  const [onlineOfferFired, setOnlineOfferFired] = useState(false);
  const [setMessage] = useState(null);
  const prevProps = useRef({ newRewardsCount });
  const isDesktopOrLarger = useMediaQuery(LG_BREAKPOINT_AND_UP);

  onlineOffers = onlineOffers ?? [];
  patronRewards = patronRewards ?? [];
  plsRewards = plsRewards ?? [];
  partnerRewards = partnerRewards ?? [];

  const location = getPreferredLocationData(userData?.user?.preferredLocation);
  const locationUrl = location?.url;
  const isPreferredLocationPls = isLocationPls(location);
  const totalRewards = [
    ...partnerRewards,
    ...onlineOffers,
    ...(isPreferredLocationPls ? plsRewards : patronRewards)
  ];
  const rewardTimeZoneHelpText =
    patronRewardsData?.rewardsWithTimeZoneV2?.patronRewards
      ?.rewardTimeZoneHelpText ||
    plsRewardsData?.rewardsWithTimeZoneV2?.plsPrizeAssignments
      ?.rewardTimeZoneHelpText;

  let runningTimeOut = null;
  const resetNewRewardsCount = () => {
    if (runningTimeOut || !resetNewState || newRewardsCount === 0) {
      return;
    }

    runningTimeOut = setTimeout(() => {
      if (
        !resetNewState ||
        (!patronRewardsData?.rewardsWithTimeZoneV2?.patronRewards?.patronRewards
          ?.length &&
          !patronRewardsData?.rewardsWithTimeZoneV2?.partnerRewards
            ?.partnerRewards?.length &&
          !plsRewardsData?.rewardsWithTimeZoneV2?.plsPrizeAssignments
            ?.prizeAssignments?.length)
      ) {
        return;
      }
      resetNewState()
        .then(resp => {
          setNewRewardsCount(0);
        })
        .catch(e => console.error(e))
        .finally(() => (runningTimeOut = null));
    }, 10000);
  };

  useEffect(() => {
    if (prevProps.current.newRewardsCount !== newRewardsCount) {
      resetNewRewardsCount();
    }
  });

  useEffect(() => {
    const unsubscribeFromNewRewards = subscribeToNewRewards();
    const unsubscribeToNewPLSRewards = subscribeToNewPLSRewards();

    return () => {
      unsubscribeFromNewRewards && unsubscribeFromNewRewards();
      unsubscribeToNewPLSRewards && unsubscribeToNewPLSRewards();
    };
  }, [subscribeToNewRewards, subscribeToNewPLSRewards, plsRewards]);

  const handleRedeemOnlineOffer = (onlineOfferUuid, quantity) => {
    if (onlineOfferFired) {
      return;
    }

    setOnlineOfferFired(true);

    redeemOnlineOffer(onlineOfferUuid, quantity)
      .then(resp => {
        if (resp && resp.data && resp.data.redeemOnlineOffer) {
          const { success, message } = resp.data.redeemOnlineOffer;
          if (!success) {
            setMessage(
              message || 'Failed to redeem reward, please try again later.'
            );
          } else {
          }
        }
      })
      .catch(() => {
        setMessage('Failed to redeem reward, please try again later.');
      })
      .finally(() => setOnlineOfferFired(false));
  };

  const CurrentRewards = () => {
    const filteredPatronRewards = patronRewards.filter(r => isAvailable(r));
    const filteredOnlineOffers = onlineOffers.filter(
      r => isAvailable(r) && r.redeemed === 0
    );
    const filteredPlsRewards = plsRewards.filter(r => isAvailable(r));
    const filteredPartnerRewards = partnerRewards.filter(r => isAvailable(r));
    const rewards = [
      ...filteredPartnerRewards,
      ...(isLocationPls(location) ? filteredPlsRewards : filteredPatronRewards)
    ];

    return (
      <>
        <div className="section-title">Available Rewards</div>
        <span className="section-subtext">
          Congratulations! You have rewards available to redeem at a Wind Creek
          location.
        </span>
        <div className="rewards-list">
          {rewards.map((reward, i) => (
            <ProfileRewardCard reward={reward} key={i} />
          ))}
          <GetMoreRewardsCard isPreferredLocationPls={isPreferredLocationPls} />
        </div>
        <div className="section-title">Offers for You</div>
        <span className="section-subtext">
          Upgrade your Wind Creek experience with these offers.
        </span>
        <div className="rewards-list">
          {filteredOnlineOffers.map((reward, i) => (
            <ProfileRewardCard
              reward={reward}
              key={i}
              onlineOfferFired={onlineOfferFired}
              onRedeemOnlineOffer={handleRedeemOnlineOffer}
            />
          ))}
        </div>
      </>
    );
  };

  const PastRewards = () => {
    const pastRewards = totalRewards
      .filter(
        reward =>
          (isRedeemed(reward) && isStandardPrizeType(reward)) ||
          isExpired(reward) ||
          reward.canceled
      )
      .sort((lhs, rhs) => rhs.redeemedDate - lhs.redeemedDate);

    const subtext = pastRewards.length
      ? 'These are your recently used or expired rewards.'
      : 'You do not have any recently used or expired rewards.';

    return (
      <>
        <div className="section-title">Past Rewards</div>
        <span className="section-subtext">{subtext}</span>
        <div className="rewards-list">
          {pastRewards.map((reward, i) => (
            <ProfileRewardCard
              redeemed={isRedeemed(reward)}
              canceled={reward.canceled}
              reward={reward}
              key={i}
            />
          ))}
          <GetMoreRewardsCard isPreferredLocationPls={isPreferredLocationPls} />
        </div>
      </>
    );
  };

  return (
    <div className="profile-rewards-page-container">
      <TabView
        defaultTab={CURRENT}
        tabTitles={TABS}
        id="rewards-navigation"
        className="nav-container"
        theme="rewards"
        navClassName="rewards-tab-nav"
        matchesBreakpoint={isDesktopOrLarger}
      >
        <div className="tab-content-container profile-details-page">
          <span className="tz-help-text">{rewardTimeZoneHelpText}</span>
          <Tab.Content>
            <Tab.Pane eventKey={CURRENT} mountOnEnter>
              <h2 className="sr-only">{CURRENT}</h2>
              <CurrentRewards />
            </Tab.Pane>
            <Tab.Pane eventKey={PAST} mountOnEnter>
              <h2 className="sr-only">{PAST}</h2>
              <PastRewards />
            </Tab.Pane>
            <div className="your-casino-container">
              <img
                className="your-casino-icon"
                src={location?.image}
                alt={`${location?.name} icon`}
              />
              <div className="your-casino-text">
                Discover what's happening at your casino & plan a visit
              </div>
              <a
                className="your-casino-link"
                type="button"
                href={locationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                YOUR CASINO
              </a>
            </div>
          </Tab.Content>
        </div>
      </TabView>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  setNewRewardsCount: count => dispatch(setNewRewardsCount(count))
});

const mapStateToProps = createStructuredSelector({
  newRewardsCount: selectNewRewardsCount()
});

ProfileRewards.propTypes = {
  userData: PropTypes.shape({
    user: userShape,
    loading: PropTypes.bool,
    error: errorShape
  }),
  onlineOffers: PropTypes.arrayOf(PropTypes.shape(onlineOfferPropTypes))
    .isRequired,
  patronRewards: PropTypes.arrayOf(PropTypes.shape(patronRewardPropTypes))
    .isRequired,
  partnerRewards: PropTypes.arrayOf(PropTypes.shape(partnerRewardPropTypes)),
  plsRewards: PropTypes.arrayOf(PropTypes.shape(plsRewardPropTypes)),
  resetNewState: PropTypes.func.isRequired,
  newRewardsCount: PropTypes.number.isRequired,
  subscribeToNewRewards: PropTypes.func.isRequired,
  setNewRewardsCount: PropTypes.func.isRequired,
  redeemOnlineOffer: PropTypes.func
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileRewards);
